import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import MediaPlanContent from "./media/MediaPlanContent";
import ResumeManager from "./HiringStages/ResumeScreeningPage";
import { jobService } from "../services/job.service";
import OfferLetterPage from './HiringStages/OfferLetterPage';
import JobDescription from "./jobDescription/JobDescription";
import PipelineBuilder from "./HiringStages/PipelineBuilder";
import PipelineStageTab from "./HiringStages/PipelineStageTab";
import JobQuestionnaire from "./HiringStages/JobQuestionnaire";
import CareersPublishCard from "./jobDescription/CareersPublishCard";
import TopThree from "./rounds/TopThree";
import RoundBoards from "./rounds/RoundBoards";
import api from "../services/api";
import { ExistingCvsButton, ExistingCvsOffer } from "../components/talent/ExistingCvMatches";
import { Button, Card, EmptyState, Icon, StatusPill, Tabs } from "../ds";
import { Page, Loading, Note } from "../components/page";

// The vacancy's state as a pill: open is good news, closed is neutral, a
// draft still has details to add.
const statusTone = (status) => {
  const s = String(status || '').toLowerCase();
  if (['active', 'open', 'published', 'live'].includes(s)) return 'success';
  if (['incomplete', 'draft', 'paused', 'on hold'].includes(s)) return 'warning';
  if (['closed', 'filled', 'archived'].includes(s)) return 'neutral';
  return 'info';
};

const STORAGE_KEY = 'jobDescription';

const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || "Job Description";
  // Just created (CreateJobDescription adds ?new=1): offer the existing CVs once (F11).
  const [offerExistingCvs, setOfferExistingCvs] = useState(searchParams.get('new') === '1');
  // Bumped when people are added from the existing CVs, so the Resumes tab
  // shows them without a reload.
  const [candidatesVersion, setCandidatesVersion] = useState(0);
  const candidatesAdded = () => setCandidatesVersion((v) => v + 1);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [jobData, setJobData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inputContent, setInputContent] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [effectivePipeline, setEffectivePipeline] = useState(null); // { isCustom, stages }


  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Fetch job data with localStorage sync
  const fetchJobData = async () => {
    try {
      setLoading(true);
      
      // Try to get data from localStorage first
      const cachedData = localStorage.getItem(`${STORAGE_KEY}_${id}`);
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        setJobData(parsed);
        setGeneratedContent(parsed.generatedContent);
      }

      // Fetch fresh data from API
      const response = await jobService.getJob(id);
      const freshData = response.data;

      // Update localStorage with fresh data
      localStorage.setItem(`${STORAGE_KEY}_${id}`, JSON.stringify(freshData));
      
      setJobData(freshData);
      setGeneratedContent(freshData.generatedContent);

      // Fetch effective pipeline to build dynamic tabs
      try {
        const pRes = await api.get(`/jobs/${id}/effective-pipeline`);
        setEffectivePipeline(pRes.data?.data || null);
      } catch (e) {
        console.warn('Could not load effective pipeline', e);
      }
    } catch (err) {
      setError("Failed to load job details");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Update localStorage when content changes
  useEffect(() => {
    if (generatedContent && jobData?._id) {
      const updatedData = {
        ...jobData,
        generatedContent
      };
      localStorage.setItem(`${STORAGE_KEY}_${jobData._id}`, JSON.stringify(updatedData));
    }
  }, [generatedContent, jobData]);

  useEffect(() => {
    if (id) {
      fetchJobData();
    }
  }, [id]);

  const handleGenerateContent = async () => {
    if (!inputContent.trim()) return;

    setIsGenerating(true);
    try {
      // Prepare existing content for continuation
      const existingContent = generatedContent ? {
        overview: generatedContent.overview,
        qualifications: generatedContent.qualifications,
        responsibilities: generatedContent.responsibilities,
        requirements: generatedContent.requirements
      } : null;

      const response = await jobService.continueJobDescription(id, {
        user_message: inputContent.trim(), // Only send if not empty
        existing_content: existingContent
      });

      console.log('Full response:', response);
      console.log('Response type:', typeof response);
      console.log('Response.content:', response?.content);
      console.log('Response.content.content:', response?.content?.content);
      console.log('Response.data:', response?.data);

      if (response?.content?.content) {
        const newContent = response.content.content;
        setGeneratedContent({
          overview: newContent.overview,
          qualifications: newContent.qualifications,
          responsibilities: newContent.responsibilities,
          requirements: newContent.requirements
        });
        setInputContent("");
      } else if (response?.data?.content) {
        // Try alternative response structure
        const newContent = response.data.content;
        setGeneratedContent({
          overview: newContent.overview,
          qualifications: newContent.qualifications,
          responsibilities: newContent.responsibilities,
          requirements: newContent.requirements
        });
        setInputContent("");
      } else {
        console.error('Unexpected response structure:', response);
        throw new Error('Received invalid response format from the service');
      }
    } catch (err) {
      setError(
        err.response?.data?.message || 
        err.message || 
        "Failed to enhance job description. Please try again later."
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveContent = async (shouldContinue = false) => {
    // If there's no new generated content but user clicked Save & Continue, just navigate
    if (!generatedContent) {
      if (shouldContinue) handleTabChange("Media Plan & Content");
      return;
    }

    setIsSaving(true);
    try {
      await jobService.updateJobDescription(id, {
        overview: generatedContent.overview,
        qualifications: generatedContent.qualifications,
        responsibilities: generatedContent.responsibilities,
        requirements: generatedContent.requirements
      });

      // Update localStorage after successful save
      const updatedData = {
        ...jobData,
        jobDescription: {
          ...jobData.jobDescription,
          overview: generatedContent.overview,
          generatedQualifications: generatedContent.qualifications,
          generatedResponsibilities: generatedContent.responsibilities,
          requirements: generatedContent.requirements
        }
      };
      localStorage.setItem(`${STORAGE_KEY}_${id}`, JSON.stringify(updatedData));

      // Refresh job data to show updated content
      await fetchJobData();
      
      setGeneratedContent(null);

      if (shouldContinue) {
        handleTabChange("Media Plan & Content");
      }
    } catch (err) {
      setError("Failed to save content. Please try again.");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };


  
  // ── Dynamic tabs based on effective pipeline ─────────────────────────────
  const isCustomPipeline = effectivePipeline?.isCustom === true;
  const pipelineStages   = effectivePipeline?.stages || [];

  const tabs = [
    "Job Description",
    "Media Plan & Content",
    "Assessment Pipeline",
    "Resumes",
    // Only show stage tabs when a custom pipeline is explicitly configured
    ...(isCustomPipeline
      ? pipelineStages.filter(s => s.enabled !== false).map(s => s.label.replace(/\s*\(Arya\)/gi, ''))
      : []
    ),
    // The top 3 and one board per round (HIR-HD5, HIR-HR33)
    "Rounds",
    "Offer Letter",
  ];

  const renderTabContent = () => {
    // Fixed tabs
    switch (activeTab) {
      case "Assessment Pipeline":
        return <PipelineBuilder jobId={id} onSaved={() => {
          // Re-fetch pipeline so tabs update immediately after saving
          api.get(`/jobs/${id}/effective-pipeline`)
            .then(r => setEffectivePipeline(r.data?.data || null))
            .catch(() => {});
        }} />;
      case "Job Description":
        return (
          <div style={{ display: 'grid', gap: 24 }}>
            {/* Publication sits above the description: it is the switch that
                makes this vacancy public, and doubles as its listed/unlisted
                status at a glance. */}
            {/* @story HIR-HR1 */}
            <CareersPublishCard
              jobId={id}
              careers={jobData?.careers}
              hasDescription={!!(jobData?.jobDescription || generatedContent)}
              onSaved={(careers) => setJobData(prev => (prev ? { ...prev, careers } : prev))}
            />
            <JobDescription jobData={jobData} jobId={id} onSaved={fetchJobData} />
          </div>
        );
      case "Media Plan & Content":
        return <MediaPlanContent />;
      case "Resumes":
        return <ResumeManager refreshKey={candidatesVersion} />;
      case "Questionnaire":
        return <JobQuestionnaire />;
      case "Offer Letter":
        return <OfferLetterPage jobId={id} />;
      case "Rounds":
        return (
          <div style={{ display: 'grid', gap: 24 }}>
            <TopThree jobId={id} />
            <RoundBoards jobId={id} />
          </div>
        );
      default:
        break;
    }

    // Pipeline stage tabs — match by label (normalize display-only strips like "(Arya)")
    const stage = pipelineStages.find(s => s.label.replace(/\s*\(Arya\)/gi, '') === activeTab);
    if (stage) return <PipelineStageTab stage={stage} pipeline={pipelineStages} />;

    return <EmptyState title="Select a tab" />;
  };

  // The record's card, as on a person's page in People: title, the line
  // under it, its state and actions, then the tabs that switch views.
  const renderHeader = () => (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-display-md)', fontWeight: 700, color: 'var(--text-1)', margin: 0 }}>{jobData.title}</h1>
          {jobData.department && (
            <p style={{ color: 'var(--text-muted)', margin: '2px 0 0' }}>{jobData.department}</p>
          )}
        </div>
        <StatusPill tone={statusTone(jobData.status)}>{jobData.status}</StatusPill>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* F11: run this vacancy against the CVs the school already has. */}
          <ExistingCvsButton jobId={jobData._id} jobTitle={jobData.title} onAdded={candidatesAdded} />
          <Button size="sm" variant="secondary" onClick={() => navigate(`/jobs/${jobData._id}/reports`)}>
            View assessment reports →
          </Button>
        </div>
      </div>
      <div style={{ marginTop: 16, overflowX: 'auto' }}>
        <Tabs items={tabs.map((t) => ({ value: t, label: t }))} value={activeTab} onChange={handleTabChange} />
      </div>
    </Card>
  );

  if (loading) {
    return <Card><Loading label="Loading this vacancy" /></Card>;
  }

  if (error) {
    return <Note tone="danger">{error}</Note>;
  }

  if (!jobData) {
    return (
      <Card>
        <EmptyState icon={<Icon name="warning-circle" size={36} />} title="Could not load this vacancy" />
      </Card>
    );
  }

  return (
    <Page>
      {renderHeader()}
      {offerExistingCvs && (
        <ExistingCvsOffer
          jobId={jobData._id}
          jobTitle={jobData.title}
          onDismiss={() => setOfferExistingCvs(false)}
          onAdded={candidatesAdded}
        />
      )}
      {/* "Incomplete" reads like a failure to someone who just created the
          JD. It is not — it simply means there are more details to add. Say
          so, right where they land after saving. */}
      {jobData.status === 'Incomplete' && (
        <Note tone="warning">
          Saved. This JD is a <strong>draft</strong> — it stays “Incomplete”
          until you add the remaining details on this page (description,
          pipeline, and publishing). Nothing has been lost.
        </Note>
      )}
      <div style={{ minWidth: 0 }}>{renderTabContent()}</div>
    </Page>
  );
};

export default JobDetail;
